import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * Mobile-only bar pinned to the viewport bottom (design-spec §3 "Sticky
 * mobile bottom bar"): surface, 1px top line, padding 14×18 and a bottom
 * padding that clears the iOS home indicator (`env(safe-area-inset-bottom)`).
 * Hidden from md up — desktop uses the sticky aside card instead.
 *
 * Renders a spacer of `spacerHeight` before the bar so the page's last
 * content can scroll clear of it (salon variant ≈85px, pick-a-time ≈111px).
 *
 * While the bar is visible it sets the page's `scroll-padding-bottom` to its
 * height, so a field that receives focus (Tab, or a focused first error) is
 * scrolled clear of the bar instead of under it (WCAG 2.4.11, M4 QA 8).
 */
export interface StickyBottomBarProps {
  children: ReactNode;
  /** Region name for assistive tech, e.g. "Book Atelier Noor". */
  label?: string;
  /** Tailwind height class for the in-flow spacer. */
  spacerClassName?: string;
  className?: string;
}

export function StickyBottomBar({
  children,
  label,
  spacerClassName = 'h-[96px]',
  className,
}: StickyBottomBarProps) {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!bar || !root) return;
    const previous = root.style.scrollPaddingBottom;
    const sync = () => {
      // offsetHeight is 0 while `md:hidden` applies (desktop): no padding then.
      const h = bar.offsetHeight;
      root.style.scrollPaddingBottom = h > 0 ? `${h + 12}px` : previous;
    };
    sync();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
    ro?.observe(bar);
    window.addEventListener('resize', sync);
    // Browsers skip the scroll when a focused element is already "in the
    // viewport", even if it sits under the bar: nudge it clear ourselves.
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || bar.offsetHeight === 0 || bar.contains(target) || target === document.body) return;
      const barTop = bar.getBoundingClientRect().top;
      const r = target.getBoundingClientRect();
      const overlap = r.bottom + 12 - barTop;
      // 'instant': the page's smooth scrolling would leave it under the bar for a moment.
      if (overlap > 0 && r.height < barTop) window.scrollBy?.({ top: overlap, behavior: 'instant' as ScrollBehavior });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', sync);
      document.removeEventListener('focusin', onFocusIn);
      root.style.scrollPaddingBottom = previous;
    };
  }, []);

  return (
    <>
      <div aria-hidden="true" className={cn('md:hidden', spacerClassName)} />
      <div
        ref={barRef}
        role={label ? 'region' : undefined}
        aria-label={label || undefined}
        data-sticky-bottom-bar=""
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 border-t border-sl-line bg-sl-surface px-[18px] pt-3.5 pb-safe md:hidden',
          className
        )}
      >
        {children}
      </div>
    </>
  );
}
